class OkazoShellHeader extends HTMLElement {
  constructor() {
    super();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onHeaderFocusIn = this.onHeaderFocusIn.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
    this.updateHeaderHeight = this.updateHeaderHeight.bind(this);
    this.queueHeaderHeightUpdate = this.queueHeaderHeightUpdate.bind(this);
  }

  connectedCallback() {
    if (this.connection) return;
    this.connection = new AbortController();
    const { signal } = this.connection;

    const cartLink = this.querySelector("#cart-icon-bubble");
    if (cartLink) {
      const updateCartLabel = () => {
        const badge = cartLink.querySelector(".cart-count-bubble span[aria-hidden='true']");
        const count = Number(
          badge?.textContent ?? cartLink.querySelector("[data-okz-cart-count]")?.dataset.okzCartCount,
        );
        if (Number.isFinite(count))
          cartLink.setAttribute("aria-label", `Panier, ${count} article${count > 1 ? "s" : ""}`);
      };
      this.cartObserver = new MutationObserver(updateCartLabel);
      this.cartObserver.observe(cartLink, { childList: true, subtree: true, characterData: true });
      updateCartLabel();
    }
    this.mobileMenu = this.querySelector("[data-okz-mobile-menu]");
    this.mobileGroups = Array.from(this.querySelectorAll(".okz-shell-header__mobile-group"));
    this.navDropdowns = Array.from(this.querySelectorAll("[data-okz-nav-dropdown]"));
    this.desktopPointer = window.matchMedia("(min-width: 960px) and (hover: hover)");
    this.stickyViewport = window.matchMedia("(min-height: 501px)");
    this.lastScrollY = Math.max(0, window.scrollY);
    this.revealedAtScrollY = null;
    this.scrollIntent = null;
    this.scrollIntentExpiresAt = 0;
    this.lastTouchY = null;
    this.scrollFrame = null;
    this.headerHeightFrame = null;

    if ("ResizeObserver" in window) {
      this.headerResizeObserver = new ResizeObserver(this.queueHeaderHeightUpdate);
      this.headerResizeObserver.observe(this);
    }

    this.navDropdowns.forEach((details) => {
      const item = details.closest(".okz-shell-header__nav-item") || details;
      details.addEventListener(
        "toggle",
        () => {
          details.querySelector(":scope > summary")?.setAttribute("aria-expanded", String(details.open));
          if (details.open) {
            this.setCompact(false);
            this.navDropdowns.forEach((candidate) => {
              if (candidate !== details) candidate.open = false;
            });
          }
        },
        { signal },
      );

      item.addEventListener(
        "pointerenter",
        () => {
          if (!this.desktopPointer.matches) return;
          window.clearTimeout(details.okazoCloseTimeout);
          details.okazoOpenTimeout = window.setTimeout(() => {
            details.open = true;
          }, 120);
        },
        { signal },
      );

      item.addEventListener(
        "pointerleave",
        () => {
          if (!this.desktopPointer.matches) return;
          window.clearTimeout(details.okazoOpenTimeout);
          details.okazoCloseTimeout = window.setTimeout(() => {
            if (!item.contains(document.activeElement)) details.open = false;
          }, 160);
        },
        { signal },
      );

      item.addEventListener(
        "focusout",
        (event) => {
          if (!item.contains(event.relatedTarget)) details.open = false;
        },
        { signal },
      );
    });

    this.mobileGroups.forEach((details) => {
      details.addEventListener(
        "toggle",
        () => {
          details.querySelector(":scope > summary")?.setAttribute("aria-expanded", String(details.open));
          if (!details.open) return;
          this.mobileGroups.forEach((candidate) => {
            if (candidate !== details) candidate.open = false;
          });
        },
        { signal },
      );
    });

    if (this.mobileMenu) {
      this.mobileMenu.addEventListener("toggle", () => this.syncMobileMenu(), { signal });
    }

    this.addEventListener("focusin", this.onHeaderFocusIn, { signal });
    document.addEventListener("click", this.onDocumentClick, { signal });
    document.addEventListener("keydown", this.onDocumentKeydown, { signal });
    window.addEventListener("scroll", this.onScroll, { signal, passive: true });
    window.addEventListener("wheel", this.onWheel, { signal, passive: true });
    window.addEventListener("touchstart", this.onTouchStart, { signal, passive: true });
    window.addEventListener("touchmove", this.onTouchMove, { signal, passive: true });
    window.addEventListener("resize", this.queueHeaderHeightUpdate, { signal });
    this.desktopPointer.addEventListener("change", this.onViewportChange, { signal });
    this.stickyViewport.addEventListener("change", this.onViewportChange, { signal });
    this.syncMobileMenu();
    this.updateHeaderHeight();
    this.updateCompactState(true);
  }

  disconnectedCallback() {
    this.connection?.abort();
    this.connection = null;

    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    if (this.headerHeightFrame) window.cancelAnimationFrame(this.headerHeightFrame);
    this.headerResizeObserver?.disconnect();
    this.cartObserver?.disconnect();
    this.navDropdowns?.forEach((details) => {
      window.clearTimeout(details.okazoOpenTimeout);
      window.clearTimeout(details.okazoCloseTimeout);
    });
    document.documentElement.classList.remove("okz-shell-menu-open");
  }

  onDocumentClick(event) {
    if (this.contains(event.target)) return;
    this.closeDesktopMenus();
    this.closeMobileMenu();
  }

  onDocumentKeydown(event) {
    this.recordKeyboardScrollIntent(event);

    if (event.key === "Tab" && this.mobileMenu?.open && !this.desktopPointer.matches) {
      this.trapMobileFocus(event);
      return;
    }

    if (event.key !== "Escape") return;

    const openDropdown = this.navDropdowns.find((details) => details.open);
    if (openDropdown) {
      openDropdown.open = false;
      openDropdown.querySelector("summary")?.focus();
      return;
    }

    if (this.mobileMenu?.open) {
      this.closeMobileMenu(true);
    }
  }

  onHeaderFocusIn() {
    this.setCompact(false);
  }

  onWheel(event) {
    if (Math.abs(event.deltaY) < 2) return;
    this.recordScrollIntent(event.deltaY > 0 ? "down" : "up");
  }

  onTouchStart(event) {
    this.lastTouchY = event.touches?.[0]?.clientY ?? null;
  }

  onTouchMove(event) {
    const touchY = event.touches?.[0]?.clientY;

    if (touchY === undefined || this.lastTouchY === null) {
      this.lastTouchY = touchY ?? null;
      return;
    }

    const deltaY = this.lastTouchY - touchY;
    if (Math.abs(deltaY) > 2) this.recordScrollIntent(deltaY > 0 ? "down" : "up");
    this.lastTouchY = touchY;
  }

  recordKeyboardScrollIntent(event) {
    if (event.defaultPrevented) return;

    switch (event.key) {
      case "ArrowDown":
      case "PageDown":
      case "End":
        this.recordScrollIntent("down");
        break;
      case "ArrowUp":
      case "PageUp":
      case "Home":
        this.recordScrollIntent("up");
        break;
      case " ":
      case "Spacebar":
        this.recordScrollIntent(event.shiftKey ? "up" : "down");
        break;
      default:
        break;
    }
  }

  recordScrollIntent(intent) {
    this.scrollIntent = intent;
    this.scrollIntentExpiresAt = performance.now() + 280;
  }

  getScrollIntent(isMovingUp, isMovingDown) {
    if (performance.now() <= this.scrollIntentExpiresAt) return this.scrollIntent;
    if (isMovingUp) return "up";
    if (isMovingDown) return "down";
    return null;
  }

  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateCompactState();
    });
  }

  onViewportChange() {
    if (this.desktopPointer.matches) {
      this.closeMobileMenu();
    } else {
      this.closeDesktopMenus();
    }
    this.queueHeaderHeightUpdate();
    this.updateCompactState(true);
  }

  updateCompactState(force = false) {
    const scrollY = Math.max(0, window.scrollY);
    const isMovingUp = scrollY < this.lastScrollY - 4;
    const isMovingDown = scrollY > this.lastScrollY + 4;
    const scrollIntent = this.getScrollIntent(isMovingUp, isMovingDown);
    const shouldReveal = scrollIntent === "up" && (isMovingUp || this.revealedAtScrollY !== null);
    const isIntentMovingDown = scrollIntent === "down" && isMovingDown;
    const hasOpenSurface =
      this.mobileMenu?.open ||
      this.navDropdowns.some((details) => details.open) ||
      Boolean(this.querySelector("predictive-search[open]"));
    const hasFocus = this.contains(document.activeElement);

    if (!this.stickyViewport.matches || scrollY < 180 || hasOpenSurface || hasFocus) {
      this.revealedAtScrollY = null;
      this.setCompact(false);
    } else if (shouldReveal) {
      this.revealedAtScrollY = scrollY;
      this.setCompact(false);
    } else if (this.revealedAtScrollY !== null) {
      if (isIntentMovingDown && scrollY > this.revealedAtScrollY + 80) {
        this.revealedAtScrollY = null;
        this.setCompact(true);
      } else {
        this.setCompact(false);
      }
    } else if (force || scrollY > 280) {
      this.setCompact(true);
    }

    this.lastScrollY = scrollY;
  }

  setCompact(compact) {
    if (compact) this.revealedAtScrollY = null;
    this.classList.toggle("is-compact", compact);
    this.dataset.compact = String(compact);
    this.queueHeaderHeightUpdate();
  }

  queueHeaderHeightUpdate() {
    if (this.headerHeightFrame) return;
    this.headerHeightFrame = window.requestAnimationFrame(() => {
      this.headerHeightFrame = null;
      this.updateHeaderHeight();
    });
  }

  updateHeaderHeight() {
    const height = this.stickyViewport?.matches ? this.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty("--okz-shell-header-height", `${Math.max(0, Math.ceil(height))}px`);
  }

  closeDesktopMenus() {
    this.navDropdowns.forEach((details) => {
      details.open = false;
    });
  }

  closeMobileMenu(returnFocus = false) {
    if (!this.mobileMenu?.open) return;
    this.mobileMenu.open = false;
    this.mobileGroups.forEach((details) => {
      details.open = false;
    });
    if (returnFocus) this.mobileMenu.querySelector(":scope > summary")?.focus();
  }

  syncMobileMenu() {
    if (!this.mobileMenu) return;
    const summary = this.mobileMenu.querySelector(":scope > summary");
    summary?.setAttribute("aria-label", this.mobileMenu.open ? "Fermer le menu" : "Ouvrir le menu");
    summary?.setAttribute("aria-expanded", String(this.mobileMenu.open));
    if (this.mobileMenu.open) this.setCompact(false);
    document.documentElement.classList.toggle(
      "okz-shell-menu-open",
      this.mobileMenu.open && !this.desktopPointer.matches,
    );
  }

  trapMobileFocus(event) {
    const focusable = Array.from(
      this.mobileMenu.querySelectorAll(
        'summary, a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((element) => element.getClientRects().length > 0);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !this.mobileMenu.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get("okazo-shell-header")) {
  customElements.define("okazo-shell-header", OkazoShellHeader);
}

class OkazoShellFooter extends HTMLElement {
  constructor() {
    super();
    this.syncGroups = this.syncGroups.bind(this);
  }

  connectedCallback() {
    if (this.connection) return;
    this.connection = new AbortController();
    const { signal } = this.connection;

    this.groups = Array.from(this.querySelectorAll("[data-okz-footer-group]"));
    this.desktop = window.matchMedia("(min-width: 768px)");

    this.querySelector('a[href="#top"]')?.addEventListener(
      "click",
      (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        document.querySelector(".okz-shell-header__logo")?.focus({ preventScroll: true });
        window.scrollTo({
          top: 0,
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",
        });
      },
      { signal },
    );

    this.desktop.addEventListener("change", this.syncGroups, { signal });
    this.syncGroups();
  }

  disconnectedCallback() {
    this.connection?.abort();
    this.connection = null;
  }

  syncGroups() {
    this.groups.forEach((group) => {
      group.open = this.desktop.matches;
    });
  }
}

if (!customElements.get("okazo-shell-footer")) {
  customElements.define("okazo-shell-footer", OkazoShellFooter);
}
